import React, { useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import history from '../history';
import { logoutUser } from '../actions';
import { getProductConfig } from '../backend/productConfig';
import { SETUP_MODULES, getSetupComponent, getSetupRight } from './registry';
import { SetupNavContext, confirmDiscardIfDirty, setupPersistedTab, saveSetupTab, clearSetupStorage, resolveDisp, isMasterRole } from './setupCommon';
import { ConfirmHost } from './confirmService';
import SetupModuleStub from './SetupModuleStub';
import './setupStyle.css';

/**
 * SetupView — the ONE rails-driven Setup shell, shared across product modes
 * (unified-viewer-setup-design.md §1.3 / §4.1).
 *
 * The nav is built from `productConfig.setup.modules` via the registry; each entry renders its
 * real component (registry) or SetupModuleStub when stubbed / unimplemented. Entry is gated on
 * the ADMIN right (mirrors Java SetupController.checkHasRight(ADMIN)). Cloud enables Setup;
 * LOCAL/eview keep `setup.enabled:false` (byte-identical) until their port lands.
 *
 * Styling follows the house system (docs/ui-styling-guidelines.md): className CSS in
 * setupStyle.css scoped under `.setup-container`, fluid clamp() sizing, mobile-first. The
 * stacked-vs-side-by-side topbar is a CSS structural query, not a JS branch — one DOM here.
 */
const normalize = (modules) =>
  (modules || []).map((m) => {
    const id = typeof m === 'string' ? m : m.id;
    const isStub = typeof m === 'object' && !!m.stub;
    return { id, isStub, label: (SETUP_MODULES[id] && SETUP_MODULES[id].label) || id };
  });

// Per-module gate (David 2026-08-05): the Setup shell is ALWAYS open to every user — a module's own
// right decides whether ITS tab shows. Display (+ any future viewer pref) carries no right → always
// visible; config modules carry 'ADMIN'. So a non-admin just sees fewer tabs, never a locked-out shell.
const canSeeModule = (id, props) => {
  const mod = SETUP_MODULES[id] || {};
  const right = getSetupRight(id);
  const isMaster = isMasterRole(props.bRole);   // honors the view-as-local QA aid
  const fAdmin = props.fAdmin === 1 || props.fAdmin === true;
  // Layer 1 — the door (fAdmin): an ADMIN-gated module needs fAdmin (a local site-admin is bRole 0 +
  // fAdmin 1) or master; a local viewer (bRole 0 + fAdmin 0) is kept out of the admin modules and just
  // sees Display. Display carries no right → open to everyone. Unknown right fails closed.
  if (right === 'ADMIN' && !(isMaster || fAdmin)) return false;
  if (right && right !== 'ADMIN') return false;
  // Layer 2 — access: past the door, a module's own access:{master,local} can still hide its tab for THIS
  // viewer via resolveDisp (e.g. a master-only module carries local:'hidden'). Default access → shown.
  return resolveDisp(mod, isMaster) !== 'hidden';
};

const Gate = ({ title, msg, onLive }) => (
  <div className="setup-container setup-gate">
    <div className="setup-gate__title">{title}</div>
    <div className="setup-gate__msg">{msg}</div>
    <button className="setup-nav-btn" onClick={onLive}>Back to Live</button>
  </div>
);

const SetupView = (props) => {
  const cfg = getProductConfig().setup || { enabled: false, modules: [] };
  // Only the modules THIS user may see (Display always; admin modules for admins) — the shell itself
  // is never gated (David 2026-08-05).
  const items = normalize(cfg.modules).filter((it) => canSeeModule(it.id, props));
  // Land on the first REAL (implemented) module so entry never opens on a "not yet
  // implemented" stub; falls back to the first tab. As stubs become real, the default
  // naturally shifts left to the first working screen (Cameras today; Customers once org lands).
  const firstReal = items.find((i) => !i.isStub && getSetupComponent(i.id));
  // activeId + optional cross-tab params — a screen can hand the next one context (e.g. Device →
  // "show THIS device's cameras"); the params ride the nav so the target module reads them on mount.
  const [nav, setNav] = useState(() => {
    const defaultId = (firstReal || items[0] || {}).id || null;
    const restoredId = setupPersistedTab(items.map((i) => i.id));
    return { id: restoredId || defaultId, params: null };
  });

  // The active screen reports its unsaved-edits state here; guardNav confirms before navigating
  // away (switching the top tab, Live, Logout, or a cross-tab jump) so a dirty screen can't be left
  // silently.
  const dirtyRef = useRef(false);
  const reportDirty = useCallback((d) => { dirtyRef.current = d; }, []);
  const guardNav = useCallback(async (fn) => {
    if (!(await confirmDiscardIfDirty(dirtyRef.current))) return;
    dirtyRef.current = false;
    fn();
  }, []);
  // Switch tabs, optionally handing the target module params. A plain tab click passes none (which
  // clears any stale params); a screen deep-links another via go(id, {…}).
  const go = useCallback((id, params) => guardNav(() => { saveSetupTab(id); setNav({ id, params: params || null }); }), [guardNav]);

  // Back returns to wherever Setup was opened FROM (Live or Playback), not always Live (David 2026-08-05):
  // Setup is reached via history.push('/setup'), so goBack() pops to the pusher. If Setup was loaded
  // fresh (refresh / deep link → history.action !== 'PUSH') there's nothing to pop, so fall back to Live.
  const enteredViaPush = useRef(history.action === 'PUSH');
  const goLive = () => { clearSetupStorage(); history.push('/live'); };
  const goBack = () => { clearSetupStorage(); if (enteredViaPush.current) history.goBack(); else history.push('/live'); };
  // Logout from the shell top-right (all products; David 2026-08-05) — drop the session + return to the
  // login route. Routed through guardNav (below) so unsaved edits are confirmed before the session ends.
  const doLogout = () => { clearSetupStorage(); if (props.logoutUser) props.logoutUser(props.sSess); history.push('/'); };
  // No whole-shell gate (David 2026-08-05): the shell is always open; access is per-MODULE (canSeeModule
  // / getSetupRight). The only "unavailable" case is a product that declares NO setup modules at all —
  // Display is ungated + in every product's module list, so this is effectively never hit.
  if (items.length === 0) {
    return <Gate title="Setup unavailable" msg="Setup is not available for this viewer." onLive={goLive} />;
  }

  const active = items.find((i) => i.id === nav.id) || items[0];
  const renderContent = () => {
    if (!active) return null;
    const Comp = active.isStub ? null : getSetupComponent(active.id);
    return Comp ? <Comp /> : <SetupModuleStub id={active.id} label={active.label} />;
  };

  return (
    <div className="setup-container">
      {/* Mobile portrait stacks the chrome: brand+actions on one row, a horizontally-scrollable
          pill strip below (so the tabs never wrap and push the actions off-screen). The
          stacked → single-row switch is handled by a CSS structural query at larger sizes. */}
      <div className="setup-topbar">
        <div className="setup-topbar__header-row">
          <div className="setup-topbar__brand">
            {/* Back to Live (#86bb5dx62 David): a back affordance top-left replaces the Live +
                Logout buttons — Setup is always entered FROM Live, and Logout lives on the Live
                account menu. Routes through guardNav so unsaved edits are confirmed first. */}
            <button
              className="setup-back-btn"
              onClick={() => guardNav(goBack)}
              aria-label="Back"
              title="Back"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <span className="setup-topbar__title">Setup</span>
          </div>
          {/* Logout — top-right of the shell for every product (David 2026-08-05). guardNav confirms
              unsaved edits before the session ends. */}
          <div className="setup-topbar__actions">
            <button
              className="setup-topbar__logout"
              onClick={() => guardNav(doLogout)}
              aria-label="Log out"
              title="Log out"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" /></svg>
              <span className="setup-topbar__logout-label">Logout</span>
            </button>
          </div>
        </div>
        <div className="setup-tabs">
          {items.map((it) => {
            const isActive = active && active.id === it.id;
            const cls = 'setup-tabs__tab'
              + (isActive ? ' setup-tabs__tab--active' : '')
              + (it.isStub && !isActive ? ' setup-tabs__tab--stub' : '');
            return (
              <button
                key={it.id}
                className={cls}
                onClick={() => go(it.id)}
                title={it.isStub ? 'Not yet implemented' : it.label}
              >
                {it.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="setup-content">
        <SetupNavContext.Provider value={{ reportDirty, go, params: nav.params }}>{renderContent()}</SetupNavContext.Provider>
      </div>
      {/* one shared confirm dialog for the whole Setup subsystem (discard / delete / reset) */}
      <ConfirmHost />
    </div>
  );
};

const mapStateToProps = (state) => ({
  bRole: state.user.bRole,     // 0/1 master bit — a master sees every Setup module
  fAdmin: state.user.fAdmin,   // ADMIN right — grants Setup access to a non-master site-admin
  sSess: state.auth && state.auth.sSess,
});

const mapDispatchToProps = (dispatch) => ({
  logoutUser: (sSess) => dispatch(logoutUser(sSess)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SetupView);
